import React, { useState, useEffect, useCallback } from 'react';
import POFormModal from './POFormModal';
import POPreview from './POPreview';

const OrderPage = ({ currentUser }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFormModal, setShowFormModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [searchFilters, setSearchFilters] = useState({
    poNumber: '',
    supplier: '',
    status: ''
  });

  // สถานะการติดตาม
  const statusOptions = [
    'ขอซื้อ',
    'ส่งออเดอร์แล้ว',
    'กำลังสั่งซื้อ',
    'จัดส่ง',
    'ถึงคลัง',
    'รับเข้าแล้ว'
  ];

  // API Base URL
  const API_BASE = process.env.REACT_APP_API_URL || '';

  // ดึงข้อมูล PO จาก API
  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_BASE}/api/purchase-orders`);
      if (!response.ok) {
        throw new Error('Failed to fetch purchase orders');
      }
      const data = await response.json();
      setOrders(data);
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError('ไม่สามารถโหลดข้อมูล PO ได้');
    } finally {
      setLoading(false);
    }
  }, [API_BASE]);

  // โหลดข้อมูลเมื่อ component mount
  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleAdd = () => {
    setEditingOrder(null);
    setShowFormModal(true);
    setError(null);
  };

  const handleEdit = (order) => {
    setEditingOrder(order);
    setShowFormModal(true);
    setError(null);
  };

  const handlePreview = (order) => {
    setSelectedOrder(order);
    setShowPreviewModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('คุณต้องการลบ PO นี้หรือไม่?')) {
      try {
        setError(null);
        const response = await fetch(`${API_BASE}/api/purchase-orders/${id}`, {
          method: 'DELETE'
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to delete purchase order');
        }

        await fetchOrders();
      } catch (err) {
        console.error('Error deleting order:', err);
        setError(err.message || 'ไม่สามารถลบข้อมูลได้');
      }
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'ขอซื้อ': return 'bg-dark';
      case 'ส่งออเดอร์แล้ว': return 'bg-info';
      case 'กำลังสั่งซื้อ': return 'bg-warning';
      case 'จัดส่ง': return 'bg-primary';
      case 'ถึงคลัง': return 'bg-secondary';
      case 'รับเข้าแล้ว': return 'bg-success';
      default: return 'bg-light';
    }
  };

  const getProductTypeLabel = (type) => {
    switch (type) {
      case 'battery': return 'แบตเตอรี่';
      case 'charger': return 'ชาร์จเจอร์';
      case 'accessory': return 'อุปกรณ์เสริม';
      default: return type;
    }
  };

  const handleSearchChange = (e) => {
    const { name, value } = e.target;
    setSearchFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // กรองข้อมูล PO ตามเงื่อนไขการค้นหา
  const filteredOrders = orders.filter(order => {
    return (
      order.po_number.toLowerCase().includes(searchFilters.poNumber.toLowerCase()) &&
      order.supplier.toLowerCase().includes(searchFilters.supplier.toLowerCase()) &&
      order.status.includes(searchFilters.status)
    );
  });

  // แสดง loading
  if (loading && orders.length === 0) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <span className="ms-2 thai-text">กำลังโหลดข้อมูล...</span>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3 className="thai-text" style={{ backgroundColor: 'transparent' }}>
          <i className="fas fa-clipboard-list me-2"></i>
          จัดการออเดอร์ (PO)
        </h3>
        <button className="btn btn-primary" onClick={handleAdd}>
          <i className="fas fa-plus me-2"></i>
          <span className="thai-text">สร้าง PO ใหม่</span>
        </button>
      </div>

      {/* แสดง Error Alert */}
      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          <i className="fas fa-exclamation-circle me-2"></i>
          <span className="thai-text">{error}</span>
          <button type="button" className="btn-close" onClick={() => setError(null)}></button>
        </div>
      )}

      {/* การค้นหา */}
      <div className="card mb-4">
        <div className="card-header bg-light">
          <h6 className="mb-0 thai-text">
            <i className="fas fa-search me-2"></i>
            ค้นหา
          </h6>
        </div>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-label thai-text">เลขที่ PO</label>
              <input
                type="text"
                className="form-control"
                name="poNumber"
                value={searchFilters.poNumber}
                onChange={handleSearchChange}
                placeholder="ค้นหาเลขที่ PO..."
              />
            </div>
            <div className="col-md-3">
              <label className="form-label thai-text">ผู้จำหน่าย</label>
              <input
                type="text"
                className="form-control"
                name="supplier"
                value={searchFilters.supplier}
                onChange={handleSearchChange}
                placeholder="ค้นหาผู้จำหน่าย..."
              />
            </div>
            <div className="col-md-3">
              <label className="form-label thai-text">สถานะ</label>
              <select
                className="form-select"
                name="status"
                value={searchFilters.status}
                onChange={handleSearchChange}
              >
                <option value="">ทั้งหมด</option>
                {statusOptions.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>
            <div className="col-md-3 d-flex align-items-end">
              <button
                type="button"
                className="btn btn-outline-secondary w-100"
                onClick={() => setSearchFilters({
                  poNumber: '',
                  supplier: '',
                  status: ''
                })}
              >
                <i className="fas fa-times me-1"></i>
                <span className="thai-text">ล้าง</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ตาราง PO */}
      <div className="card">
        <div className="card-header bg-primary text-white">
          <h5 className="mb-0 thai-text">
            <i className="fas fa-list me-2"></i>
            รายการ PO ({filteredOrders.length} รายการ)
          </h5>
        </div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="table-dark">
                <tr>
                  <th className="thai-text">เลขที่ PO</th>
                  <th className="thai-text">ผู้จำหน่าย</th>
                  <th className="thai-text">รายการสินค้า</th>
                  <th className="thai-text text-center">จำนวนรวม</th>
                  <th className="thai-text">วันที่สั่ง</th>
                  <th className="thai-text text-center">สถานะ</th>
                  <th className="thai-text text-center" style={{ width: '150px' }}>จัดการ</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-5">
                      <i className="fas fa-clipboard-list fa-3x text-muted mb-3"></i>
                      <p className="text-muted mb-0 thai-text">
                        {orders.length === 0 ? 'ยังไม่มีข้อมูล PO' : 'ไม่พบข้อมูลที่ตรงกับเงื่อนไข'}
                      </p>
                    </td>
                  </tr>
                ) : (
                  filteredOrders.map((order) => (
                    <tr key={order.id}>
                      <td>
                        <button
                          className="btn btn-link p-0 text-decoration-none fw-bold"
                          onClick={() => handlePreview(order)}
                        >
                          {order.po_number}
                        </button>
                      </td>
                      <td>{order.supplier}</td>
                      <td>
                        {order.items && order.items.length > 0 ? (
                          <div>
                            {order.items.slice(0, 2).map((item, idx) => (
                              <span key={idx} className="badge bg-light text-dark me-1 mb-1">
                                {getProductTypeLabel(item.productType)}: {item.brand} {item.model}
                              </span>
                            ))}
                            {order.items.length > 2 && (
                              <span className="badge bg-secondary">+{order.items.length - 2} รายการ</span>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td className="text-center">
                        <span className="badge bg-primary">
                          {order.total_quantity || order.items?.reduce((sum, item) => sum + (parseInt(item.quantity) || 0), 0) || 0}
                        </span>
                      </td>
                      <td>{order.order_date ? new Date(order.order_date).toLocaleDateString('th-TH') : '-'}</td>
                      <td className="text-center">
                        <span className={`badge ${getStatusBadgeClass(order.status)} thai-text`}>
                          {order.status}
                        </span>
                      </td>
                      <td className="text-center">
                        <div className="btn-group btn-group-sm">
                          <button
                            className="btn btn-outline-info"
                            onClick={() => handlePreview(order)}
                            title="ดูรายละเอียด / PDF"
                          >
                            <i className="fas fa-file-pdf"></i>
                          </button>
                          <button
                            className="btn btn-outline-primary"
                            onClick={() => handleEdit(order)}
                            title="แก้ไข"
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                          <button
                            className="btn btn-outline-danger"
                            onClick={() => handleDelete(order.id)}
                            title="ลบ"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="mt-3 d-flex justify-content-between align-items-center">
        <p className="thai-text mb-0">จำนวน PO ทั้งหมด: {orders.length} รายการ</p>
        <button className="btn btn-sm btn-outline-primary" onClick={fetchOrders}>
          <i className="fas fa-sync-alt me-1"></i>
          <span className="thai-text">รีเฟรช</span>
        </button>
      </div>

      {/* Modal Components */}
      <POFormModal
        show={showFormModal}
        onClose={() => setShowFormModal(false)}
        onSave={fetchOrders}
        editingOrder={editingOrder}
        currentUser={currentUser}
      />

      {showPreviewModal && (
        <POPreview
          order={selectedOrder}
          onClose={() => {
            setShowPreviewModal(false);
            setSelectedOrder(null);
          }}
        />
      )}
    </div>
  );
};

export default OrderPage;
